/**
 * Excel-upload → RuleContext parser (entrance ①, DB-free / client-side).
 *
 * Reads an uploaded CI+PL workbook (the BRITZMEDI form, or any file with the
 * same INVOICE-US + PACKING LIST layout) and reconstructs as much of a
 * `RuleContext` as the document itself contains, so the existing rule engine
 * (`gate`) can run on it. We use the SAME coordinates the merge engine writes to
 * (`CI_PL_MERGE_MAP`), in reverse — single source of truth for the layout.
 *
 * HONEST SCOPE: a CI/PL contains line items, currency, incoterm text and the two
 * documents themselves — nothing about IOR / FDA Initial Importer / importer
 * structure / tariff acknowledgement. Those fields are left empty here and are
 * checked only on the form-input ("딜 입력") path. The UploadValidatePage banner
 * states this explicitly.
 *
 * PURE module: browser + Node safe.
 */

import type {
  Country,
  Deal,
  DealLineItem,
  Incoterm,
  IncotermCode,
  RuleContext,
} from "@/types";
import { CI_PL_MERGE_MAP, SHEET_NAME_INVOICE, SHEET_NAME_PACKING } from "@/lib/merge";
import {
  loadWorkbook,
  readSheet,
  resolveSheetPart,
  readCellString,
  readCellNumber,
  serialToDate,
  type SheetReader,
} from "./xlsx-read";

/** Per-field read report so the UI can show what was / was not extracted. */
export interface ParseField {
  label: string;
  value: string;
  /** true when a value was found in the document. */
  read: boolean;
}

export interface ParseReport {
  /** Sheets located by name (or fallback). */
  sheetsFound: { invoice: boolean; packing: boolean };
  /** Field-level read results (for the "what we could read" table). */
  fields: ParseField[];
  /** Number of product lines extracted. */
  lineCount: number;
  /** Non-fatal warnings (e.g. CI↔PL line mismatch in shape). */
  warnings: string[];
}

export interface ParseResult {
  ctx: RuleContext;
  report: ParseReport;
}

/** The 11 valid incoterm codes (parsing guard). */
const INCOTERM_CODES = new Set<IncotermCode>([
  "EXW", "FCA", "FAS", "FOB", "CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP",
]);

/** Sea/inland-waterway-only incoterms (for the parsed-incoterm transport_mode). */
const SEA_ONLY = new Set<IncotermCode>(["FAS", "FOB", "CFR", "CIF"]);

/**
 * Parse the incoterms remark line, e.g.
 *   "INCOTERMS : FCA ICN Airport, Incheon (Incoterms® 2020)"
 * into { code, named_place, version, mode }. Best-effort.
 */
function parseIncotermRemark(remark: string): {
  code: IncotermCode | null;
  namedPlace: string | null;
  version: string | null;
  transportHint: "air" | "sea" | null;
} {
  const text = remark.replace(/^.*?:\s*/, "").trim(); // drop "INCOTERMS :"
  if (!text) return { code: null, namedPlace: null, version: null, transportHint: null };

  // version: "(Incoterms® 2020)" or "Incoterms 2020"
  const verMatch = text.match(/Incoterms[®\s]*\s*(\d{4})/i);
  const version = verMatch ? verMatch[1] ?? null : null;

  // strip the trailing "(Incoterms® 2020)" parenthetical for place parsing
  const withoutVer = text.replace(/\(?\s*Incoterms[®\s]*\s*\d{4}\s*\)?/i, "").trim();

  // first token = incoterm code
  const firstToken = (withoutVer.split(/\s+/)[0] ?? "").toUpperCase() as IncotermCode;
  const code = INCOTERM_CODES.has(firstToken) ? firstToken : null;

  // rest = named place
  const rest = code ? withoutVer.slice(firstToken.length).trim() : withoutVer.trim();
  const namedPlace = rest.length ? rest : null;

  // transport hint from the named place text (airport keyword => air)
  let transportHint: "air" | "sea" | null = null;
  if (/airport|공항|air\b/i.test(rest)) transportHint = "air";
  else if (/\bport\b|항\b|seaport/i.test(rest)) transportHint = "sea";

  return { code, namedPlace, version, transportHint };
}

/** Read every product line out of one sheet's line table. */
function readLines(
  sheet: SheetReader,
  table: { start_row: number; end_row: number; cols: Record<string, string> },
): Array<Record<string, string | number | null>> {
  const rows: Array<Record<string, string | number | null>> = [];
  for (let r = table.start_row; r <= table.end_row; r++) {
    const row: Record<string, string | number | null> = {};
    let any = false;
    for (const [col, field] of Object.entries(table.cols)) {
      const ref = `${col}${r}`;
      // numeric fields read as number; description / cbm_text as string.
      if (field === "description_en" || field === "cbm_text") {
        const v = readCellString(sheet, ref);
        row[field] = v;
        if (v.trim() !== "") any = true;
      } else {
        const v = readCellNumber(sheet, ref);
        row[field] = v;
        if (v != null) any = true;
      }
    }
    if (any) rows.push(row);
  }
  return rows;
}

/**
 * Build a partial RuleContext from an uploaded CI+PL workbook.
 * Throws only when the workbook is unreadable as a zip/xlsx.
 */
export async function parseCiPlWorkbook(
  bytes: ArrayBuffer | Uint8Array,
): Promise<ParseResult> {
  const wb = await loadWorkbook(bytes);

  const invPart = resolveSheetPart(wb, SHEET_NAME_INVOICE, CI_PL_MERGE_MAP.invoice.part);
  const pkPart = resolveSheetPart(wb, SHEET_NAME_PACKING, CI_PL_MERGE_MAP.packing.part);

  const warnings: string[] = [];
  const fields: ParseField[] = [];

  if (!invPart) {
    throw new Error(
      "INVOICE-US 시트를 찾을 수 없습니다. CI+PL 양식(INVOICE-US + PACKING LIST 시트)이 맞는지 확인하세요.",
    );
  }

  const inv = await readSheet(wb, invPart);
  const pk = pkPart ? await readSheet(wb, pkPart) : null;

  const im = CI_PL_MERGE_MAP.invoice;
  const pm = CI_PL_MERGE_MAP.packing;

  // --- header scalars (invoice) ---------------------------------------------
  const invoiceNo = readCellString(inv, cellFor(im.header.text, "doc.invoice_no") ?? "F4");
  const consigneeName = readCellString(inv, cellFor(im.header.text, "consignee.company_name") ?? "B12");
  const consigneeAddr1 = readCellString(inv, cellFor(im.header.text, "consignee.address") ?? "B13");
  const consigneeAddr2 = readCellString(inv, cellFor(im.header.text, "consignee.address2") ?? "B14");
  const shipperName = readCellString(inv, cellFor(im.header.text, "shipper.company_name") ?? "B4");
  const finalDest = readCellString(inv, cellFor(im.header.text, "doc.final_destination") ?? "D30");
  const incotermRemark = readCellString(inv, cellFor(im.header.text, "doc.incoterms_remark") ?? "G26");

  // date serial -> ISO
  const dateRef = cellFor(im.header.date, "doc.date") ?? "I4";
  const dateSerial = readCellNumber(inv, dateRef);
  const issueDate = dateSerial != null ? serialToDate(dateSerial) : "";

  const incoParsed = parseIncotermRemark(incotermRemark);

  field(fields, "송장번호(Invoice No.)", invoiceNo);
  field(fields, "발행일(Date)", issueDate);
  field(fields, "Shipper", shipperName);
  field(fields, "Consignee", consigneeName);
  field(fields, "최종 목적지", finalDest);
  field(fields, "인코텀즈", incoParsed.code ?? "");
  field(fields, "인코텀즈 지정장소", incoParsed.namedPlace ?? "");
  field(fields, "인코텀즈 버전", incoParsed.version ?? "");

  // --- line items (invoice = qty/price/amount, packing = weights/cbm) -------
  const invLines = readLines(inv, im.table);
  const pkLines = pk ? readLines(pk, pm.table) : [];

  if (pk && invLines.length !== pkLines.length && invLines.length && pkLines.length) {
    warnings.push(
      `INVOICE-US(${invLines.length}행)와 PACKING LIST(${pkLines.length}행) 품목 행 수가 다릅니다.`,
    );
  }

  const lines: DealLineItem[] = invLines.map((il, i) => {
    const pl = pkLines[i] ?? {};
    const quantity = num(il.quantity);
    const unitPrice = num(il.unit_price);
    const lineAmount = il.line_amount != null ? num(il.line_amount) : null;
    return {
      id: `upload-line-${i + 1}`,
      deal_id: "upload-deal",
      product_id: null,
      item_kind: "product",
      line_no: i + 1,
      description_en: str(il.description_en) || null,
      hs_code: null,
      quantity,
      unit: "units",
      unit_price: unitPrice,
      line_amount: lineAmount,
      net_weight_kg: pl.net_weight_kg != null ? num(pl.net_weight_kg) : null,
      gross_weight_kg: pl.gross_weight_kg != null ? num(pl.gross_weight_kg) : null,
      weight_unit: "kg",
      package_count: null,
      package_type: null,
      cbm: parseCbm(str(pl.cbm_text)),
      is_no_commercial_value: false,
      created_at: new Date().toISOString(),
    };
  });

  // currency: not a discrete cell on this form — infer USD when the sheet is
  // INVOICE-US, else leave empty (rule AW_CURRENCY_MISSING will then fire).
  const currency = invPart === CI_PL_MERGE_MAP.invoice.part || /invoice-us/i.test(SHEET_NAME_INVOICE)
    ? "USD"
    : "";
  field(fields, "통화(currency)", currency || "(추정 불가)");
  field(fields, "품목 행 수", String(lines.length));

  // --- assemble a partial RuleContext ---------------------------------------
  const incoCode = incoParsed.code;
  const incoterm: Incoterm | null = incoCode
    ? buildIncotermStub(incoCode, incoParsed.transportHint)
    : null;

  const deal: Deal = {
    id: "upload-deal",
    deal_no: invoiceNo.replace(/^CI-/, "") || "UPLOAD",
    title: `엑셀 업로드: ${invoiceNo || "(번호 없음)"}`,
    status: "ship_prep",
    // Destination is inferred from the printed text, NOT a verified country FK.
    // We only set "US" when the document literally names the United States, so the
    // US-regulatory rules stay OFF for non-US / unknown docs (avoids false BLOCKs).
    destination_country: /united states|미국|\bUSA?\b/i.test(finalDest) ? "US" : "",
    shipper_profile_id: "",
    buyer_id: null,
    consignee_id: null,
    notify_id: null,
    importer_of_record_id: null,
    fda_initial_importer_id: null,
    importer_structure: null,
    incoterm_id: incoCode,
    incoterm_named_place: incoParsed.namedPlace,
    incoterm_named_place_type: incoParsed.transportHint === "air" ? "airport" : null,
    incoterm_version: incoParsed.version ?? "",
    transport_mode: incoParsed.transportHint,
    cargo_type: null,
    payment_term_id: null,
    currency,
    claim_preferential: false,
    insurance_obligation_party: null,
    insured_amount: null,
    export_declaration_status: null,
    fob_value_krw: null,
    payment_received: false,
    intended_use_text: null,
    // tariff_ack is an exporter acknowledgement, not present in the document.
    // Leave true so the upload check does NOT raise the (form-path) tariff ack
    // ERROR — that gate belongs to the "딜 입력" screen, per the banner.
    tariff_ack: true,
    export_declaration_no: null,
    transport_doc_no: null,
    insurance_policy_no: null,
    lc_expiry_date: null,
    lc_latest_shipment_date: null,
    contract_id: null,
    notes: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  // Minimal country shell: required_documents reflect that CI + PL are present
  // (so DM_REQUIRED_SET does not falsely flag them). No tariff data (DP-3).
  const country: Country = {
    id: deal.destination_country || "",
    name_en: finalDest || "",
    name_ko: null,
    default_currency: currency || null,
    required_documents: [
      { doc_type: "commercial_invoice", requirement: "required" },
      { doc_type: "packing_list", requirement: "required" },
    ],
    recommended_incoterms: [],
    cert_requirements: [],
    regulatory_notes: null,
    tariff_note: null,
    tariff_verified_at: null,
    tariff_source_url: null,
    // Upload path: importer parties are unknowable from the CI/PL document, so
    // for a US destination surface the honest REQ_IMPORTER_ROLE BLOCK (these
    // need the 딜 입력 screen). Other destinations stay silent — the uploaded
    // doc carries no country profile.
    required_importer_roles:
      (deal.destination_country || "") === "US"
        ? ["importer_of_record", "fda_initial_importer"]
        : [],
    is_sanctioned: false,
    language: "en",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const ctx: RuleContext = {
    deal,
    lines,
    partners: {
      consignee: consigneeName
        ? {
            id: "upload-consignee",
            company_name: consigneeName,
            company_name_local: null,
            country: deal.destination_country || "",
            address: [consigneeAddr1, consigneeAddr2].filter(Boolean).join(" "),
            contact_person: null,
            email: null,
            phone: null,
            tax_vat_id: null,
            eori: null,
            roles: ["consignee"],
            is_importer_of_record: false,
            is_fda_initial_importer: false,
            fda_initial_importer_fei: null,
            us_physical_address: null,
            grade: null,
            importer_ids: {},
            sanction_check_status: "not_checked",
            sanction_checked_at: null,
            created_at: new Date().toISOString(),
          }
        : null,
    },
    incoterm,
    country,
    products: [],
    // Both documents are physically present (this IS a CI+PL workbook).
    documents: [
      {
        id: "upload-doc-ci",
        deal_id: deal.id,
        doc_type: "commercial_invoice",
        template_id: null,
        doc_no: invoiceNo || null,
        current_version: 1,
        status: "issued",
        created_at: new Date().toISOString(),
      },
      {
        id: "upload-doc-pl",
        deal_id: deal.id,
        doc_type: "packing_list",
        template_id: null,
        doc_no: invoiceNo || null,
        current_version: 1,
        status: "issued",
        created_at: new Date().toISOString(),
      },
    ],
  };

  const report: ParseReport = {
    sheetsFound: { invoice: true, packing: !!pk },
    fields,
    lineCount: lines.length,
    warnings,
  };
  if (!pk) warnings.push("PACKING LIST 시트를 찾지 못해 중량/CBM 정합 검사는 생략됩니다.");
  if (lines.length === 0) warnings.push("품목 행을 한 줄도 읽지 못했습니다. 양식의 품목 영역을 확인하세요.");

  return { ctx, report };
}

// --- helpers -----------------------------------------------------------------

function cellFor(map: Record<string, string>, path: string): string | null {
  for (const [ref, p] of Object.entries(map)) if (p === path) return ref;
  return null;
}

function field(list: ParseField[], label: string, value: string): void {
  const v = (value ?? "").trim();
  list.push({ label, value: v, read: v !== "" && v !== "(추정 불가)" });
}

function str(v: unknown): string {
  return v == null ? "" : String(v);
}

function num(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** "0.216 CBM" -> 0.216 ; "" -> null. */
function parseCbm(text: string): number | null {
  const m = text.match(/([\d.]+)/);
  return m ? Number(m[1]) : null;
}

/**
 * Build a minimal Incoterm record for the parsed code so incoterm rules
 * (IC_KOREAN_FOB_AIR, IC_MODE_MISMATCH, etc.) can evaluate. Only the fields the
 * rule engine reads are meaningful; the rest are conservative defaults.
 */
function buildIncotermStub(
  code: IncotermCode,
  transportHint: "air" | "sea" | null,
): Incoterm {
  const isSeaOnly = SEA_ONLY.has(code);
  return {
    id: code,
    full_name: code,
    name_ko: null,
    group_code: ((): Incoterm["group_code"] => {
      if (code === "EXW") return "E";
      if (["FCA", "FAS", "FOB"].includes(code)) return "F";
      if (["CFR", "CIF", "CPT", "CIP"].includes(code)) return "C";
      return "D";
    })(),
    transport_mode: isSeaOnly ? "sea" : "any",
    risk_transfer_point: "",
    cost_split_point: "",
    risk_cost_separated: ["CFR", "CIF", "CPT", "CIP"].includes(code),
    export_clearance_by: code === "EXW" ? "B" : "S",
    import_clearance_by: code === "DDP" ? "S" : "B",
    seller_insurance_obligation:
      code === "CIF" ? "ICC_C" : code === "CIP" ? "ICC_A" : "none",
    insurance_min_coverage_pct: code === "CIF" || code === "CIP" ? 110 : null,
    suitable_cargo: "any",
    seller_unloads: code === "DPU",
    notes: transportHint ? `parsed transport hint: ${transportHint}` : null,
  };
}
